import React from 'react';

/* ====================================================================
   THE LAST THING BETWEEN A BUG AND A BLANK SCREEN

   React unmounts the entire tree when a render throws. There was no
   boundary anywhere in this app, so any such error took the whole screen
   with it: a white page, no message, and nothing to press. The only way
   back was for somebody to work out on their own that a refresh would fix
   it — at the club, mid-competition, on a phone.

   These were not hypothetical. A refused request handed an error object to
   a list, the next render called .map on it, and the app vanished. That
   particular cause is fixed at the source (see apiJson and apiList in
   api.js), but "a render threw" is not a thing an app can promise never to
   do again, and the cost of being wrong should be a message rather than a
   blank page.

   What it deliberately does NOT do is hide the error. The message says
   what went wrong, because an admin ringing up about a problem that
   describes itself gets help faster than one about "it went white".
   ==================================================================== */

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The console is the only place a stack is any use, so keep it whole.
    console.error('A screen failed to draw:', error, info && info.componentStack);
    /* And tell whoever runs GreenSheet, rather than waiting for a member to.
       The message and where it broke only — never what was on the screen.
       Fire and forget: reporting a crash must not cause another. */
    try {
      fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: String((error && error.message) || error || '').slice(0, 300),
          path: window.location.pathname,
          componentStack: String((info && info.componentStack) || '').slice(0, 1200),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) { /* nothing more to do */ }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    /* Try Again re-renders the same children with the same props. If
       whatever caused this is still true it will simply fail again, which
       is honest — but a great many render errors come from one bad piece of
       data in state, and going back to the previous screen clears it. */
    const tryAgain = () => this.setState({ error: null });

    return (
      <div className="w3-container" style={{ maxWidth: 'var(--gs-content-narrow, 640px)', margin: '0 auto', padding: 'var(--gs-space-8, 32px) var(--gs-space-4, 16px)' }}>
        <div className="w3-card w3-round w3-white" style={{ overflow: 'hidden' }}>
          <div className="w3-container w3-theme-d4" style={{ padding: 'var(--gs-space-4, 16px) var(--gs-space-6, 24px)' }}>
            <h3 style={{ margin: 0 }}>
              <i className="fa fa-exclamation-triangle w3-margin-right" aria-hidden="true"></i>
              This screen stopped working
            </h3>
          </div>
          <div className="w3-container" style={{ padding: 'var(--gs-space-6, 24px)' }}>
            <p style={{ marginTop: 0 }}>
              Something went wrong drawing this page. Nothing you had already saved is affected.
            </p>
            <p className="w3-small w3-text-grey">
              Try again, and if it keeps happening, reload the app. Telling whoever
              looks after your GreenSheet what you were doing — and the message
              below — will help them find it.
            </p>
            <pre style={{
              background: 'var(--gs-bg-alt, #f4f6f7)',
              border: '1px solid var(--gs-border, #dde3e6)',
              borderRadius: 'var(--gs-radius-sm, 4px)',
              padding: 'var(--gs-space-3, 12px)',
              fontSize: 'var(--gs-text-xs, 12px)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: '0 0 var(--gs-space-4, 16px)',
            }}>{String((error && error.message) || error)}</pre>
            <div style={{ display: 'flex', gap: 'var(--gs-space-2, 8px)', flexWrap: 'wrap' }}>
              <button type="button" className="w3-button w3-round w3-theme-d3" onClick={tryAgain}>
                <i className="fa fa-refresh w3-margin-right" aria-hidden="true"></i>Try again
              </button>
              <button type="button" className="w3-button w3-round w3-border"
                onClick={() => { window.location.href = '/'; }}>
                Reload the app
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
